import {
  Box,
  Button,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useCallback } from 'react';

import { AppShell } from '../components/app-shell/AppShell';
import {
  AvailabilityCalendar,
  type CalendarEntry,
  type CalendarExchange,
} from '../components/calendar/AvailabilityCalendar';
import { EmptyState, MotionSection, PageHero } from '../components/ui';
import { ApiError, fetchOk } from '../lib/api';
import { csrfHeaders, useAuth } from '../lib/auth-context';
import { showToast } from '../lib/toast';

export const Route = createFileRoute('/availability')({
  component: ArtistAvailabilityPage,
});

type Visibility = 'public' | 'venues_only' | 'private';

type CalendarData = {
  entries: CalendarEntry[];
  exchanges: CalendarExchange[];
  defaultState: 'available' | 'unavailable';
  visibility: Visibility;
};

const VISIBILITY_OPTIONS: Array<{ label: string; value: Visibility }> = [
  { label: 'Public', value: 'public' },
  { label: 'Venues only', value: 'venues_only' },
  { label: 'Private', value: 'private' },
];

const VISIBILITY_BLURB: Record<Visibility, string> = {
  public: 'Anyone viewing your profile can see when you’re free.',
  venues_only: 'Only hosts can see your availability on your profile.',
  private: 'Just for you. Your calendar stays off your public profile.',
};

function ArtistAvailabilityPage(): React.JSX.Element {
  const { capabilities, isLoading } = useAuth();
  const isArtist = capabilities?.isArtist === true;

  return (
    <AppShell tone="content">
      <Stack gap="xl">
        <PageHero
          eyebrow="Touring"
          title="Availability"
          lede="Paint the days you’re free or away. Confirmed stays show automatically, so this is the one place you can see your whole road."
        />
        {isLoading ? (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        ) : isArtist ? (
          <MotionSection>
            <AvailabilityEditor />
          </MotionSection>
        ) : (
          <MotionSection>
            <Box style={{ maxWidth: 640, marginInline: 'auto' }}>
              <EmptyState
                title="Set up your artist profile first."
                body="Your availability calendar lives on your artist profile. Once that’s ready, you can mark the days you’re on the road."
                action={
                  <Button
                    component={Link}
                    to="/profile"
                    variant="gradient"
                    gradient={{ from: 'brand.6', to: 'sun.5', deg: 35 }}
                  >
                    Go to profile
                  </Button>
                }
              />
            </Box>
          </MotionSection>
        )}
      </Stack>
    </AppShell>
  );
}

function AvailabilityEditor(): React.JSX.Element {
  const queryClient = useQueryClient();

  const calendarQuery = useQuery<CalendarData>({
    queryKey: ['artist-calendar'],
    queryFn: async () => {
      const res = await fetchOk('/api/artist-calendar/me/calendar', { credentials: 'include' });
      return (await res.json()) as CalendarData;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async (params: { dates: string[]; state: 'blocked' | 'available' }) => {
      const res = await fetchOk('/api/artist-calendar/me/calendar/entries', {
        method: 'PUT',
        credentials: 'include',
        headers: { ...csrfHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify(params),
      });
      return (await res.json()) as { ok: boolean; entries: CalendarEntry[] };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CalendarData>(['artist-calendar'], (prev) =>
        prev ? { ...prev, entries: data.entries } : prev,
      );
    },
    onError: (err) => {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't update your calendar.",
      });
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: async (visibility: Visibility) => {
      const res = await fetchOk('/api/artist-calendar/me/calendar/visibility', {
        method: 'PATCH',
        credentials: 'include',
        headers: { ...csrfHeaders(), 'content-type': 'application/json' },
        body: JSON.stringify({ visibility }),
      });
      return (await res.json()) as { ok: boolean; visibility: Visibility };
    },
    onSuccess: (data) => {
      queryClient.setQueryData<CalendarData>(['artist-calendar'], (prev) =>
        prev ? { ...prev, visibility: data.visibility } : prev,
      );
      showToast({ kind: 'success', message: 'Visibility updated.' });
    },
    onError: (err) => {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't change who sees your calendar.",
      });
    },
  });

  const handleToggleDates = useCallback(
    (dates: string[], newState: 'blocked' | 'available') => {
      toggleMutation.mutate({ dates, state: newState });
    },
    [toggleMutation],
  );

  if (calendarQuery.isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader />
      </Group>
    );
  }
  if (calendarQuery.isError || !calendarQuery.data) {
    return (
      <Text c="red" role="alert">
        We couldn’t load your calendar. Try again in a moment.
      </Text>
    );
  }

  const data = calendarQuery.data;

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Click or drag to toggle days. Teal is free, grey is away. Blue days are confirmed stays.
        They’re set by your bookings and can’t be edited here.
      </Text>
      <AvailabilityCalendar
        entries={data.entries}
        exchanges={data.exchanges}
        defaultState={data.defaultState}
        onToggleDates={handleToggleDates}
      />

      <Paper withBorder p="md" radius="md">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Who can see this?
          </Text>
          <SegmentedControl
            fullWidth
            value={data.visibility}
            data={VISIBILITY_OPTIONS}
            onChange={(value) => visibilityMutation.mutate(value as Visibility)}
            disabled={visibilityMutation.isPending}
          />
          <Text size="xs" c="dimmed">
            {VISIBILITY_BLURB[data.visibility]}
          </Text>
        </Stack>
      </Paper>
    </Stack>
  );
}
